/**
 * Version Compatibility Checker
 * Validates SDK/Server version compatibility
 */

import { CompatibilityError } from '../errors/VoiceMateErrors.js';

export class VersionChecker {
  constructor(logger) {
    this.logger = logger;
  }

  /**
   * Parse semantic version string
   * @param {string} versionString - Version string (e.g., "1.2.3")
   * @returns {Object} Parsed version {major, minor, patch}
   */
  parseVersion(versionString) {
    const parts = versionString.split('.').map(Number);
    return {
      major: parts[0] || 0,
      minor: parts[1] || 0,
      patch: parts[2] || 0
    };
  }

  /**
   * Compare two versions
   * @param {Object} v1 - First version
   * @param {Object} v2 - Second version
   * @returns {number} -1 if v1 < v2, 0 if equal, 1 if v1 > v2
   */
  compare(v1, v2) {
    if (v1.major !== v2.major) return v1.major - v2.major;
    if (v1.minor !== v2.minor) return v1.minor - v2.minor;
    return v1.patch - v2.patch;
  }

  /**
   * Check compatibility between SDK and server versions
   * @param {string} serverVersion - Server version
   * @param {string} sdkVersion - SDK version
   * @param {string} minServerVersion - Minimum required server version
   * @returns {Object} Compatibility result
   * @throws {CompatibilityError} If versions are incompatible
   */
  async checkCompatibility(serverVersion, sdkVersion, minServerVersion) {
    const server = this.parseVersion(serverVersion);
    const sdk = this.parseVersion(sdkVersion);
    const minRequired = this.parseVersion(minServerVersion);

    this.logger.debug('Version check:', {
      server: serverVersion,
      sdk: sdkVersion,
      minRequired: minServerVersion
    });

    // Check if server version >= minServerVersion
    if (this.compare(server, minRequired) < 0) {
      throw new CompatibilityError(
        `Server version ${serverVersion} is not compatible. ` +
        `SDK requires minimum server version ${minServerVersion}.`,
        { serverVersion, sdkVersion, minServerVersion }
      );
    }

    // Warn if major versions differ
    if (server.major !== sdk.major) {
      this.logger.warn(
        `SDK major version (${sdk.major}) differs from server (${server.major}). ` +
        `Some features may not work as expected.`
      );
    }

    return { compatible: true, serverVersion, sdkVersion };
  }
}
